import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, Flame, Users, Sparkles, Loader2 } from "lucide-react";
import { fetchRecipe, fetchRecipes } from "../lib/api";
import { BRAND, PRODUCTS } from "../lib/brand";
import { Navigation } from "../components/landing/Navigation";
import { Footer } from "../components/landing/Footer";
import { FloatingWhatsApp } from "../components/landing/FloatingWhatsApp";

const HeatDots = ({ level }) => (
  <span className="inline-flex items-center gap-1">
    <Flame size={13} className="text-mo-terracotta" />
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: i < level ? "#B33922" : "rgba(92,82,72,0.18)" }}
      />
    ))}
  </span>
);

const RecipeDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setNotFound(false);
    fetchRecipe(slug)
      .then(async (data) => {
        if (ignore) return;
        setRecipe(data);
        try {
          const siblings = await fetchRecipes(data.spice_slug);
          if (!ignore) setRelated(siblings.filter((s) => s.slug !== data.slug).slice(0, 2));
        } catch {
          if (!ignore) setRelated([]);
        }
      })
      .catch(() => {
        if (!ignore) setNotFound(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen bg-mo-cream">
        <Navigation />
        <div className="py-40 flex items-center justify-center text-mo-ink-soft">
          <Loader2 className="animate-spin mr-3" size={18} /> Loading recipe…
        </div>
      </main>
    );
  }

  if (notFound || !recipe) {
    return (
      <main className="min-h-screen bg-mo-cream">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">— 404</div>
          <h1 className="font-serif-display text-5xl text-mo-ink mb-6">
            That recipe is still on the stove.
          </h1>
          <p className="text-mo-ink-soft mb-8">
            We couldn't find a recipe at this address. Browse the full collection instead.
          </p>
          <Link
            to="/recipes"
            data-testid="recipe-404-back"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-mo-terracotta text-mo-cream"
          >
            <ArrowLeft size={16} /> Back to recipes
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const spice = PRODUCTS.find((p) => p.slug === recipe.spice_slug);
  const waLink = `${BRAND.whatsapp.link}?text=${encodeURIComponent(
    `Hi Maati! I'm cooking your "${recipe.name}" and would like to order ${recipe.spice_name} (100 GM).`,
  )}`;

  return (
    <main data-testid="recipe-detail-page" className="min-h-screen bg-mo-cream">
      <Navigation />

      <section className="relative pt-28 pb-10 lg:pt-32 lg:pb-14 kraft-grain">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(-1)}
            data-testid="recipe-back-btn"
            className="inline-flex items-center gap-2 text-sm text-mo-ink-soft hover:text-mo-terracotta transition-colors mb-8"
          >
            <ArrowLeft size={15} /> Back
          </button>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
                — {recipe.kicker}
              </div>
              <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-mo-ink leading-[0.98] tracking-tight">
                {recipe.name}
              </h1>
              <p className="mt-6 text-mo-ink-soft text-lg leading-relaxed max-w-xl">
                {recipe.intro}
              </p>

              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4 items-center text-sm">
                <Meta icon={Clock} label="Prep" value={recipe.prep} />
                <Meta icon={Flame} label="Cook" value={recipe.cook} />
                <Meta icon={Users} label="Serves" value={recipe.serves} />
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-mo-ink-soft">Heat</span>
                  <HeatDots level={recipe.heat} />
                </div>
              </div>

              {spice && (
                <Link
                  to="/#products"
                  data-testid="recipe-spice-chip"
                  className="mt-8 inline-flex items-center gap-3 p-2 pr-5 rounded-full border border-[#5C5248]/20 bg-mo-cream hover:border-mo-terracotta transition-colors duration-300 group"
                >
                  <div
                    className="w-10 h-10 rounded-full overflow-hidden kraft-pouch"
                    style={{ padding: "3px" }}
                  >
                    <img
                      src={spice.label}
                      alt={spice.name}
                      className="w-full h-full object-contain bg-[#FAF6EE] rounded-full"
                    />
                  </div>
                  <div className="leading-tight">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-mo-ink-soft">
                      Cooked with
                    </div>
                    <div className="font-medium text-mo-ink text-sm group-hover:text-mo-terracotta">
                      Maati {spice.name}
                    </div>
                  </div>
                </Link>
              )}
            </div>

            <div className="lg:col-span-6">
              <div className="relative">
                <img
                  src={recipe.image}
                  alt={recipe.name}
                  className="w-full aspect-[4/5] object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-5 -left-5 bg-mo-cream border border-[#5C5248]/15 px-5 py-3 rounded-full shadow-xl flex items-center gap-3">
                  <span className="font-serif-display italic text-mo-terracotta">from</span>
                  <span className="font-serif-display text-xl">Maati Kitchen</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
                — What you'll need
              </div>
              <h2 className="font-serif-display text-3xl text-mo-ink mb-6">Ingredients</h2>
              <ul data-testid="recipe-ingredients" className="space-y-3 text-sm text-mo-ink-soft">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-mo-terracotta shrink-0" />
                    <span>{ing}</span>
                  </li>
                ))}
              </ul>

              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                data-testid="recipe-order-spice"
                className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-mo-terracotta text-mo-cream text-sm hover:bg-[#8F2B17] transition-colors"
              >
                <Sparkles size={15} />
                Order {recipe.spice_name}
              </a>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
              — Method
            </div>
            <h2 className="font-serif-display text-3xl text-mo-ink mb-8">How to cook it</h2>
            <ol data-testid="recipe-steps" className="space-y-6">
              {recipe.steps.map((s, i) => (
                <li
                  key={i}
                  className="relative p-6 pl-16 rounded-2xl bg-[#EAE3D9]/60 border border-[#5C5248]/10 kraft-grain"
                >
                  <span className="absolute left-5 top-5 w-9 h-9 rounded-full bg-mo-terracotta text-mo-cream font-serif-display text-lg flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-mo-ink leading-relaxed">{s}</p>
                </li>
              ))}
            </ol>

            {recipe.tips && (
              <div className="mt-10 p-7 rounded-2xl bg-mo-green text-mo-cream">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#D99026] mb-2">
                  Chef's note
                </div>
                <p className="font-serif-display italic text-xl leading-snug">
                  "{recipe.tips}"
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="relative py-20 bg-[#EAE3D9]/60 kraft-grain">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-3">
                  — Cook with the same tin
                </div>
                <h3 className="font-serif-display text-3xl text-mo-ink">
                  More with {recipe.spice_name}
                </h3>
              </div>
              <Link
                to="/recipes"
                className="hidden sm:inline-flex items-center gap-2 text-sm text-mo-ink-soft hover:text-mo-terracotta"
              >
                All recipes <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/recipes/${r.slug}`}
                  data-testid={`related-recipe-${r.slug}`}
                  className="group overflow-hidden rounded-2xl relative aspect-[16/9]"
                >
                  <img
                    src={r.image}
                    alt={r.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C2520] via-[#2C2520]/20 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-mo-cream">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D99026]">
                      {r.kicker}
                    </div>
                    <h4 className="font-serif-display text-2xl mt-1">{r.name}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
};

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2">
    <Icon size={15} className="text-mo-green" />
    <span className="text-[10px] uppercase tracking-[0.2em] text-mo-ink-soft">{label}</span>
    <span className="text-mo-ink font-medium">{value}</span>
  </div>
);

export default RecipeDetail;
